import React from 'react';
import { motion } from 'framer-motion';

/* ============================================
   ANIMATED FLOATING ELEMENTS
   "Brain, imagination comes to life" vibe
   ============================================ */

const float = (delay = 0, duration = 6) => ({
  y: [0, -12, 0],
  transition: {
    duration,
    repeat: Infinity,
    repeatType: 'loop',
    ease: 'easeInOut',
    delay,
  },
});

const rotate = (delay = 0) => ({
  rotate: [0, 360],
  transition: {
    duration: 20,
    repeat: Infinity,
    repeatType: 'loop',
    ease: 'linear',
    delay,
  },
});

const pulse = (delay = 0) => ({
  scale: [1, 1.1, 1],
  opacity: [0.6, 1, 0.6],
  transition: {
    duration: 3,
    repeat: Infinity,
    repeatType: 'loop',
    ease: 'easeInOut',
    delay,
  },
});

/* --- Brain SVG --- */
export const AnimatedBrain = ({ className = '', size = 64 }) => (
  <motion.div animate={float(0, 7)} className={className}>
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left hemisphere */}
      <motion.path
        d="M32 14C26 14 22 16 20 19C17 16 13 17 11 20C9 23 9 27 11 30C9 32 8 35 9 38C10 41 13 43 16 43C17 46 19 48 22 49C25 50 28 49 30 47L32 45"
        stroke="#1EACEE"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="rgba(30, 172, 238, 0.08)"
        animate={{ pathLength: [0.8, 1, 0.8] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Right hemisphere */}
      <motion.path
        d="M32 14C38 14 42 16 44 19C47 16 51 17 53 20C55 23 55 27 53 30C55 32 56 35 55 38C54 41 51 43 48 43C47 46 45 48 42 49C39 50 36 49 34 47L32 45"
        stroke="#F68D2C"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="rgba(246, 141, 44, 0.08)"
        animate={{ pathLength: [0.8, 1, 0.8] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      />
      {/* Neural connections */}
      <motion.circle cx="24" cy="26" r="2" fill="#1EACEE" animate={pulse(0)} />
      <motion.circle cx="40" cy="26" r="2" fill="#F68D2C" animate={pulse(0.8)} />
      <motion.circle cx="28" cy="36" r="1.5" fill="#1EACEE" animate={pulse(1.2)} />
      <motion.circle cx="36" cy="36" r="1.5" fill="#F68D2C" animate={pulse(0.4)} />
      <motion.line x1="24" y1="26" x2="28" y2="36" stroke="#1EACEE" strokeWidth="1" opacity="0.4" />
      <motion.line x1="40" y1="26" x2="36" y2="36" stroke="#F68D2C" strokeWidth="1" opacity="0.4" />
      <motion.line x1="28" y1="36" x2="36" y2="36" stroke="#9DDAF5" strokeWidth="1" opacity="0.3" />
    </svg>
  </motion.div>
);

/* --- Robot / AI Agent SVG --- */
export const AnimatedRobot = ({ className = '', size = 60 }) => (
  <motion.div animate={float(1, 8)} className={className}>
    <svg width={size} height={size} viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Antenna */}
      <motion.line x1="30" y1="6" x2="30" y2="14" stroke="#1EACEE" strokeWidth="2" strokeLinecap="round" />
      <motion.circle cx="30" cy="5" r="3" fill="#F68D2C" animate={pulse(0)} />
      {/* Head */}
      <rect x="16" y="14" width="28" height="22" rx="6" fill="rgba(30, 172, 238, 0.1)" stroke="#1EACEE" strokeWidth="2" />
      {/* Eyes */}
      <motion.circle
        cx="23" cy="25" r="3.5"
        fill="#1EACEE"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.circle
        cx="37" cy="25" r="3.5"
        fill="#F68D2C"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
      />
      {/* Mouth / smile */}
      <path d="M24 31C26 33 34 33 36 31" stroke="#1EACEE" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      {/* Body */}
      <rect x="20" y="38" width="20" height="12" rx="4" fill="rgba(246, 141, 44, 0.08)" stroke="#F68D2C" strokeWidth="1.5" />
      {/* Body light */}
      <motion.circle cx="30" cy="44" r="2" fill="#1EACEE" animate={pulse(0.5)} />
      {/* Arms */}
      <motion.line
        x1="16" y1="40" x2="10" y2="46"
        stroke="#1EACEE" strokeWidth="2" strokeLinecap="round"
        animate={{ rotate: [0, -10, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        style={{ transformOrigin: '16px 40px' }}
      />
      <motion.line
        x1="44" y1="40" x2="50" y2="46"
        stroke="#F68D2C" strokeWidth="2" strokeLinecap="round"
        animate={{ rotate: [0, 10, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        style={{ transformOrigin: '44px 40px' }}
      />
    </svg>
  </motion.div>
);

/* --- Code Brackets / Tools SVG --- */
export const AnimatedCode = ({ className = '', size = 52 }) => (
  <motion.div animate={float(2, 6)} className={className}>
    <svg width={size} height={size} viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left bracket < */}
      <motion.path
        d="M18 14L8 26L18 38"
        stroke="#1EACEE"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        animate={{ x: [-1, 1, -1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Right bracket > */}
      <motion.path
        d="M34 14L44 26L34 38"
        stroke="#F68D2C"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        animate={{ x: [1, -1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Slash / */}
      <motion.line
        x1="28" y1="10" x2="24" y2="42"
        stroke="#1EACEE"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />
    </svg>
  </motion.div>
);

/* --- Lightbulb / Idea SVG --- */
export const AnimatedLightbulb = ({ className = '', size = 48 }) => (
  <motion.div animate={float(0.5, 5)} className={className}>
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Glow */}
      <motion.circle
        cx="24" cy="20" r="15"
        fill="rgba(246, 141, 44, 0.08)"
        animate={{ scale: [1, 1.15, 1], opacity: [0.08, 0.15, 0.08] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        style={{ transformOrigin: '24px 20px' }}
      />
      {/* Bulb */}
      <path
        d="M24 8C18.5 8 14 12.5 14 18C14 22 16 24.5 18 27V30H30V27C32 24.5 34 22 34 18C34 12.5 29.5 8 24 8Z"
        stroke="#F68D2C"
        strokeWidth="2"
        fill="rgba(246, 141, 44, 0.06)"
        strokeLinecap="round"
      />
      {/* Filament */}
      <motion.path
        d="M21 22C22 20 23 22 24 20C25 22 26 20 27 22"
        stroke="#F68D2C"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Base */}
      <rect x="19" y="31" width="10" height="3" rx="1" stroke="#1EACEE" strokeWidth="1.5" fill="none" />
      <rect x="20" y="35" width="8" height="2" rx="1" stroke="#1EACEE" strokeWidth="1" fill="none" />
      {/* Rays */}
      <motion.g animate={{ opacity: [0.3, 0.8, 0.3] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}>
        <line x1="24" y1="2" x2="24" y2="5" stroke="#F68D2C" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="10" y1="10" x2="12" y2="12" stroke="#F68D2C" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="38" y1="10" x2="36" y2="12" stroke="#F68D2C" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="6" y1="20" x2="9" y2="20" stroke="#F68D2C" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="42" y1="20" x2="39" y2="20" stroke="#F68D2C" strokeWidth="1.5" strokeLinecap="round" />
      </motion.g>
    </svg>
  </motion.div>
);

/* --- Gear / Tool SVG --- */
export const AnimatedGear = ({ className = '', size = 44 }) => (
  <motion.div animate={float(1.5, 7)} className={className}>
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      animate={rotate(0)}
    >
      <path
        d="M22 8L24 4L26 8L30 6L29 10L33 10L31 14L35 16L32 18L34 22L30 22L31 26L27 25L26 29L22 27L18 29L17 25L13 26L14 22L10 22L12 18L9 16L13 14L11 10L15 10L14 6L18 8L20 4L22 8Z"
        stroke="#1EACEE"
        strokeWidth="1.5"
        fill="rgba(30, 172, 238, 0.06)"
        strokeLinejoin="round"
      />
      <circle cx="22" cy="16" r="5" stroke="#1EACEE" strokeWidth="1.5" fill="none" />
    </motion.svg>
  </motion.div>
);

/* --- Sparkle / Magic particles --- */
export const AnimatedSparkles = ({ className = '' }) => (
  <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
    {[...Array(6)].map((_, i) => (
      <motion.div
        key={`anim-${i}`}
        className="absolute"
        style={{
          left: `${15 + i * 15}%`,
          top: `${20 + (i % 3) * 25}%`,
        }}
        animate={{
          y: [0, -20, 0],
          opacity: [0, 0.8, 0],
          scale: [0.5, 1, 0.5],
        }}
        transition={{
          duration: 3 + i * 0.5,
          repeat: Infinity,
          delay: i * 0.8,
          ease: 'easeInOut',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 0L7.2 4.8L12 6L7.2 7.2L6 12L4.8 7.2L0 6L4.8 4.8L6 0Z"
            fill={i % 2 === 0 ? '#1EACEE' : '#F68D2C'}
            opacity="0.6"
          />
        </svg>
      </motion.div>
    ))}
  </div>
);

/* --- Floating Particles Background --- */
export const FloatingParticles = ({ className = '' }) => (
  <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
    {/* Small dots floating */}
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={`anim-${i}`}
        className="absolute rounded-full"
        style={{
          width: `${3 + (i % 3) * 2}px`,
          height: `${3 + (i % 3) * 2}px`,
          background: i % 2 === 0 ? 'rgba(30, 172, 238, 0.3)' : 'rgba(246, 141, 44, 0.3)',
          left: `${10 + i * 11}%`,
          top: `${15 + (i % 4) * 20}%`,
        }}
        animate={{
          y: [0, -30 - i * 5, 0],
          x: [0, (i % 2 === 0 ? 10 : -10), 0],
          opacity: [0.2, 0.7, 0.2],
        }}
        transition={{
          duration: 5 + i,
          repeat: Infinity,
          delay: i * 0.6,
          ease: 'easeInOut',
        }}
      />
    ))}
  </div>
);

/* --- Neural Network / Connection Lines --- */
export const NeuralNetwork = ({ className = '' }) => (
  <motion.div className={className} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
    <svg width="200" height="160" viewBox="0 0 200 160" fill="none" className="w-full h-auto">
      {/* Nodes */}
      {[
        { cx: 20, cy: 40, color: '#1EACEE', delay: 0 },
        { cx: 20, cy: 80, color: '#1EACEE', delay: 0.2 },
        { cx: 20, cy: 120, color: '#1EACEE', delay: 0.4 },
        { cx: 100, cy: 50, color: '#F68D2C', delay: 0.6 },
        { cx: 100, cy: 110, color: '#F68D2C', delay: 0.8 },
        { cx: 180, cy: 80, color: '#1EACEE', delay: 1 },
      ].map((node, i) => (
        <motion.circle
          key={`anim-${i}`}
          cx={node.cx}
          cy={node.cy}
          r="6"
          fill={node.color}
          opacity="0.8"
          animate={pulse(node.delay)}
        />
      ))}
      {/* Connections */}
      {[
        { x1: 26, y1: 40, x2: 94, y2: 50 },
        { x1: 26, y1: 40, x2: 94, y2: 110 },
        { x1: 26, y1: 80, x2: 94, y2: 50 },
        { x1: 26, y1: 80, x2: 94, y2: 110 },
        { x1: 26, y1: 120, x2: 94, y2: 50 },
        { x1: 26, y1: 120, x2: 94, y2: 110 },
        { x1: 106, y1: 50, x2: 174, y2: 80 },
        { x1: 106, y1: 110, x2: 174, y2: 80 },
      ].map((line, i) => (
        <motion.line
          key={`anim-${i}`}
          {...line}
          stroke={i < 6 ? '#1EACEE' : '#F68D2C'}
          strokeWidth="1"
          opacity="0.15"
          animate={{ opacity: [0.1, 0.3, 0.1] }}
          transition={{ duration: 3, repeat: Infinity, delay: i * 0.3, ease: 'easeInOut' }}
        />
      ))}
    </svg>
  </motion.div>
);
